import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight, AlertCircle, Info } from 'lucide-react';
import { cachedGet } from '../lib/apiCache';
import { Block, SmartLink } from './blocks/BlockRenderer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ButtonElement = ({ el }) => {
  const btnStyle = { background: el.style?.bg_color || '#0094ff' };
  const btn = (
    <Button className="text-white px-6" style={btnStyle} data-testid={`custom-btn-${el.id}`}>
      {el.label} {el.link && <ArrowRight className="w-4 h-4 ml-2" />}
    </Button>
  );
  if (!el.link) return <div className={`text-${el.style?.align || 'center'}`}>{btn}</div>;
  return <div className={`text-${el.style?.align || 'center'}`}><SmartLink href={el.link}>{btn}</SmartLink></div>;
};

const TextElement = ({ el }) => (
  <div className="border rounded-lg p-5"
    style={{ background: el.style?.bg_color || '#231f1f', borderColor: el.style?.border_color || 'rgba(96,94,94,0.1)' }}
    data-testid={`custom-text-${el.id}`}>
    <div className="flex items-start gap-3">
      {el.style?.show_icon !== false && <Info className="w-5 h-5 text-[#0094ff] flex-shrink-0 mt-0.5" />}
      <div className="flex-1">
        {el.label && <h3 className="text-white font-semibold text-base mb-1.5" style={{ color: el.style?.title_color || '#fff' }}>{el.label}</h3>}
        {el.content && <p className="text-[#a1a1aa] leading-relaxed text-sm whitespace-pre-line" style={{ color: el.style?.text_color || '#a1a1aa' }}>{el.content}</p>}
        {el.link && <SmartLink href={el.link} className="inline-flex items-center gap-1 mt-2 text-[#0094ff] text-sm font-medium hover:underline">Mehr erfahren <ArrowRight className="w-3 h-3" /></SmartLink>}
      </div>
    </div>
  </div>
);

const BannerElement = ({ el }) => (
  <div className="rounded-lg p-4 text-center"
    style={{ background: el.style?.bg_color || '#0094ff', color: el.style?.text_color || '#fff' }}
    data-testid={`custom-banner-${el.id}`}>
    <div className="flex items-center justify-center gap-2">
      <AlertCircle className="w-5 h-5" />
      <p className="font-medium text-sm">{el.content || el.label}</p>
      {el.link && <SmartLink href={el.link} className="underline text-sm font-semibold ml-2">Details &rarr;</SmartLink>}
    </div>
  </div>
);

const ImageElement = ({ el }) => {
  if (!el.content) return null;
  const img = <img src={el.content} alt={el.label || 'Bild'} className="rounded-lg max-w-full inline-block" style={{ maxHeight: el.style?.max_height || '400px' }} />;
  return (
    <div className={`text-${el.style?.align || 'center'}`} data-testid={`custom-img-${el.id}`}>
      {el.link ? <SmartLink href={el.link}>{img}</SmartLink> : img}
      {el.label && <p className="text-[#a1a1aa] text-sm mt-2">{el.label}</p>}
    </div>
  );
};

const DividerElement = ({ el }) => (
  <div className="py-2" data-testid={`custom-divider-${el.id}`}><hr className="border-[#605e5e]/20" /></div>
);

const ELEMENT_MAP = {
  button: ButtonElement,
  text: TextElement,
  banner: BannerElement,
  image: ImageElement,
  divider: DividerElement,
};

/** Öffentliche Bausteine einer Seite/Position (sortiert) — auch für Seiten, die selbst entscheiden müssen. */
export const usePageElements = (page, position) => {
  const [elements, setElements] = useState([]);

  const fetchElements = useCallback(async () => {
    try {
      const data = await cachedGet(`${API_URL}/api/site-elements/public`);
      const pageElements = data[page] || [];
      const filtered = position ? pageElements.filter(el => (el.position || 'bottom') === position) : pageElements;
      filtered.sort((a, b) => (a.order || 0) - (b.order || 0));
      setElements(filtered);
    } catch (e) { console.error('CustomElements laden:', e); }
  }, [page, position]);

  useEffect(() => { fetchElements(); }, [fetchElements]);
  return elements;
};

const CustomElements = ({ page, position }) => {
  const elements = usePageElements(page, position);

  if (elements.length === 0) return null;

  return (
    <div className="py-4 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid={`custom-elements-${page}-${position || 'all'}`}>
      {elements.map((el, i) => {
        // Neue Builder-Blöcke tragen `data`; ältere Elemente nutzen label/content/link/style.
        const isBlock = el.data && typeof el.data === 'object';
        const Component = ELEMENT_MAP[el.type];
        if (!isBlock && !Component) return null;
        return (
          <motion.div
            key={el.id || `el-${i}`}
            className={isBlock ? undefined : 'mb-4'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i, 5) * 0.08 }}
            data-testid={`page-block-${el.id}`}
          >
            {isBlock ? <Block block={{ id: el.id, type: el.type, data: el.data }} /> : <Component el={el} />}
          </motion.div>
        );
      })}
    </div>
  );
};

export default CustomElements;
